import { useState, useEffect } from "react";
import AppHeader from "@/components/AppHeader";
import BottomNav from "@/components/BottomNav";
import { BarChart3, Loader2, TrendingUp, Zap, Flame, Leaf, ClipboardCheck, FileText, CreditCard, MessageSquare, Share2 } from "lucide-react";
import { authFetch } from "@/utils/authFetch";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  electrical: { color: "#00A3E0", bg: "#E8F7FC", label: "Electrical", Icon: Zap },
  gas: { color: "#EF4444", bg: "#FEECEC", label: "Gas & Boiler", Icon: Flame },
  mcs: { color: "#22C55E", bg: "#EAFBF0", label: "MCS", Icon: Leaf },
  pat: { color: "#8B5CF6", bg: "#F3EFFE", label: "PAT", Icon: ClipboardCheck },
  notification: { color: "#D4A537", bg: "#FEF3C7", label: "Notifications", Icon: FileText },
};

function categorize(type) {
  if (!type) return "notification";
  if (type.startsWith("mcs_")) return "mcs";
  if (type.startsWith("gas_") || type === "gas_boiler_commissioning") return "gas";
  if (type === "pat_testing") return "pat";
  return "electrical";
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${API}/analytics`, { credentials: "include" })
      .then((r) => r.json())
      .then(setData)
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
      <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00A3E0] animate-spin" /></div>
      <BottomNav />
    </div>
  );

  const byType = data?.by_type || {};
  const byMonth = data?.by_month || {};
  const totalCerts = data?.total_certificates || 0;
  const totalNotifs = data?.total_notifications || 0;
  const monthEntries = Object.entries(byMonth);
  const maxMonth = Math.max(...Object.values(byMonth), 1);

  // Group by category
  const catTotals = { electrical: 0, gas: 0, mcs: 0, pat: 0 };
  Object.entries(byType).forEach(([type, count]) => {
    const cat = categorize(type);
    if (catTotals[cat] !== undefined) catTotals[cat] += count;
  });
  const totalAll = totalCerts + totalNotifs;

  // Top document types
  const typeEntries = Object.entries(byType).sort(([, a], [, b]) => b - a).slice(0, 10);
  const maxType = Math.max(...typeEntries.map(([, v]) => v), 1);

  const CERT_LABELS = {
    domestic_eic: "Domestic EIC", minor_works: "Minor Works", three_circuit_mw: "3 Circuit MW",
    eicr: "EICR", eic: "EIC", earthing_bonding: "Earthing & Bonding", danger_notice: "Danger Notice",
    visual_inspection: "Visual Inspection", ev_charger: "EV Charger", electrical_isolation: "Isolation",
    ev_risk_assessment: "EV Risk", ventilation: "Ventilation", landlords_interim: "Landlords", solar_pv_elec: "Solar PV",
    mcs_solar_pv_install: "MCS PV Install", mcs_solar_pv_commissioning: "MCS PV Commission",
    mcs_battery_commissioning: "MCS Battery Commission", mcs_heat_pump_commissioning: "MCS Heat Pump",
    mcs_battery_handover: "MCS Battery Handover", mcs_solar_pv_handover: "MCS PV Handover",
    gas_safety_record: "Gas Safety Record", gas_boiler_commissioning: "Boiler Commissioning",
    gas_boiler_service: "Boiler Service", gas_warranty: "Gas Warranty", gas_decommissioning: "Gas Decommission",
    pat_testing: "PAT Testing",
  };

  return (
    <div className="min-h-screen pb-16 sm:pb-0" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="analytics-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        <h1 className="text-2xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
          <BarChart3 className="w-6 h-6 inline mr-2 -mt-1 text-[#00A3E0]" />Analytics
        </h1>

        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="rounded-2xl p-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Total Documents</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{totalAll}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Certificates</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#00A3E0' }}>{totalCerts}</p>
          </div>
          <div className="rounded-2xl p-4" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>Notifications</p>
            <p className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: '#D4A537' }}>{totalNotifs}</p>
          </div>
        </div>

        {/* Category Breakdown — Donut-style */}
        <div className="rounded-2xl p-5 mb-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>By Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {Object.entries(CATEGORY_COLORS).map(([key, cfg]) => {
              const count = key === "notification" ? totalNotifs : (catTotals[key] || 0);
              return (
                <div key={key} className="text-center p-3 rounded-xl" style={{ backgroundColor: cfg.bg }} data-testid={`cat-${key}`}>
                  <cfg.Icon className="w-5 h-5 mx-auto mb-1" style={{ color: cfg.color }} />
                  <p className="text-xl font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: cfg.color }}>{count}</p>
                  <p className="text-[10px] font-semibold" style={{ color: cfg.color }}>{cfg.label}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly Trend */}
        {monthEntries.length > 0 && (
          <div className="rounded-2xl p-5 mb-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#00A3E0]" />
              <h2 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Monthly Trend</h2>
            </div>
            <div className="flex items-end gap-1.5 h-36">
              {monthEntries.map(([month, count]) => {
                const pct = (count / maxMonth) * 100;
                const label = month.length >= 7 ? `${month.slice(5)}/${month.slice(2, 4)}` : month;
                return (
                  <div key={month} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                    <span className="text-[10px] font-bold mb-1" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{count}</span>
                    <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(pct, 5)}%`, minHeight: '4px', backgroundColor: '#00A3E0' }} />
                    <span className="text-[8px] mt-1 truncate w-full text-center" style={{ color: 'var(--text-muted)' }}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Top Document Types */}
        {typeEntries.length > 0 && (
          <div className="rounded-2xl p-5 mb-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <h2 className="text-sm font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Top Document Types</h2>
            <div className="space-y-2.5">
              {typeEntries.map(([type, count]) => {
                const pct = (count / maxType) * 100;
                const cat = categorize(type);
                const cfg = CATEGORY_COLORS[cat] || CATEGORY_COLORS.electrical;
                return (
                  <div key={type} className="flex items-center gap-3">
                    <span className="text-xs font-medium w-32 truncate" style={{ color: 'var(--text-secondary)' }}>{CERT_LABELS[type] || type}</span>
                    <div className="flex-1 h-6 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--row-alt)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(pct, 3)}%`, backgroundColor: cfg.color }} />
                    </div>
                    <span className="text-xs font-bold w-8 text-right" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Credit Balances */}
        <div className="rounded-2xl p-5 mb-6" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>
            <CreditCard className="w-4 h-4 inline mr-1.5 -mt-0.5" /> Credit Balances
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: "Electrical", val: data?.electrical_credits || 0, color: "#00A3E0" },
              { label: "Gas", val: data?.gas_credits || 0, color: "#EF4444" },
              { label: "MCS", val: data?.mcs_credits || 0, color: "#22C55E" },
              { label: "PAT", val: data?.pat_credits || 0, color: "#8B5CF6" },
              { label: "SMS", val: data?.sms_credits || 0, color: "#D97706" },
            ].map((c) => (
              <div key={c.label} className="text-center p-2 rounded-xl" style={{ backgroundColor: 'var(--row-alt)' }}>
                <p className="text-lg font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: c.color }}>{c.val}</p>
                <p className="text-[10px] font-semibold" style={{ color: 'var(--text-secondary)' }}>{c.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Purchases */}
        {(data?.credit_transactions || []).length > 0 && (
          <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--bg-card)', boxShadow: '0 1px 3px var(--shadow-color)' }}>
            <h2 className="text-sm font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)' }}>Recent Purchases</h2>
            <div className="space-y-2">
              {data.credit_transactions.map((tx, i) => {
                const typeLabel = tx.credit_type === "sms" ? "SMS" : tx.credit_type === "electrical" ? "Electrical" : tx.credit_type === "mcs" ? "MCS" : tx.credit_type === "pat" ? "PAT" : tx.credit_type === "gas" ? "Gas" : tx.credit_type;
                return (
                  <div key={i} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: 'var(--border-light)' }}>
                    <div>
                      <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{typeLabel} Credits</span>
                      <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>+{tx.credits_added}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Mono, monospace', color: 'var(--text-primary)' }}>£{(tx.amount / 100).toFixed(2)}</span>
                      <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>{tx.created_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
